import { useState, useEffect, useCallback, useRef } from 'react';
import {
  initVoiceService,
  startListening,
  cancelListening,
  speakResponse,
  stopSpeaking,
  setEnrollment,
  setPreferences,
  getVoices,
  isReady,
  isListening as checkListening,
  destroyVoiceService,
} from '@/services/voice/VoiceService';
import { getVoiceProfile } from '@/services/api/voiceApi';
import type { VoiceState, VoiceEnrollment, ModelLoadingProgress } from '@/services/voice/types';
import { kokoroTTS } from '@/services/voice/KokoroWebSocketTTS';
import { agentVoiceApi } from '@/services/api/agentApi';
import { getCurrentAccessToken } from '@/services/api/auth-session';
import { useAuthStore } from '@/stores/auth/auth-store';

type AvailableVoice = Awaited<ReturnType<typeof getVoices>>[number];

export function useVoiceControl() {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [enrollment, setEnrollmentState] = useState<VoiceEnrollment | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [modelProgress, setModelProgress] = useState<ModelLoadingProgress[]>([]);
  const [availableVoices, setAvailableVoices] = useState<AvailableVoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const onTranscriptionRef = useRef<((text: string) => void) | null>(null);
  const onInterimTranscriptRef = useRef<((text: string) => void) | null>(null);
  const micPressInProgressRef = useRef(false);
  const authInitialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken ?? null);

  // Fetch enrollment after auth is ready. The profile endpoint is private, so
  // unauthenticated startup must not make a guaranteed 401 request.
  useEffect(() => {
    let mounted = true;

    if (!authInitialized) {
      return () => { mounted = false; };
    }

    if (!isAuthenticated || !accessToken) {
      setEnrollmentState(null);
      setIsEnrolled(false);
      setEnrollment(null);
      return () => { mounted = false; };
    }

    async function fetchProfile() {
      try {
        const response = await getVoiceProfile();
        if (mounted && response.success && response.data) {
          setEnrollmentState(response.data);
          setIsEnrolled(true);
          setEnrollment(response.data);
        }
      } catch {
        // Not enrolled yet - that's fine
      }
    }

    fetchProfile();
    return () => { mounted = false; };
  }, [accessToken, authInitialized, isAuthenticated]);

  // Load available TTS voices
  useEffect(() => {
    getVoices().then(setAvailableVoices).catch(() => {});
  }, []);

  const initialize = useCallback(async () => {
    if (isReady()) return;
    initializedRef.current = false;

    try {
      await initVoiceService(
        {
          onStateChange: setVoiceState,
          onModelProgress: (progress) => {
            setModelProgress(prev => {
              const existing = prev.findIndex(p => p.model === progress.model);
              if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = progress;
                return updated;
              }
              return [...prev, progress];
            });
          },
          onTranscription: (result) => {
            onTranscriptionRef.current?.(result.text);
          },
          onInterimTranscript: (text) => {
            onInterimTranscriptRef.current?.(text);
          },
          onVerificationFailed: (confidence) => {
            setError(`Voice not recognized (confidence: ${(confidence * 100).toFixed(0)}%)`);
            setTimeout(() => setError(null), 3000);
          },
          onError: (err) => {
            setError(err);
            setTimeout(() => setError(null), 5000);
          },
        },
        enrollment,
      );
      initializedRef.current = true;
      kokoroTTS.setToken(getCurrentAccessToken());

      // Auto-enable Kokoro TTS if the user's personal agent has a voice blend configured
      try {
        const res = await agentVoiceApi.personalAgent();
        if (res.success && res.data?._id && res.data.voice_config?.blend?.length) {
          kokoroTTS.setAgentId(res.data._id);
          setPreferences({ useKokoroTTS: true, kokoroAgentId: res.data._id });
        }
      } catch {
        // No personal agent or no blend — use browser TTS
      }
    } catch (err) {
      setError(String(err));
    }
  }, [enrollment]);

  const handleMicPress = useCallback(async () => {
    // Guard against double-invocation while async operation is in progress
    if (micPressInProgressRef.current) {
      console.log('[useVoiceControl] handleMicPress blocked - already in progress');
      return;
    }
    micPressInProgressRef.current = true;

    try {
      // If not enrolled, show enrollment dialog
      if (!isEnrolled) {
        setShowEnrollment(true);
        return;
      }

      // Initialize if not ready (token fetch or Whisper fallback)
      if (!isReady()) {
        await initialize();
        if (!isReady()) {
          return;
        }
      }

      // Toggle: press once to start listening, press again to cancel
      const listening = checkListening();
      if (listening) {
        await cancelListening();
      } else {
        setError(null);
        await startListening();
      }
    } finally {
      micPressInProgressRef.current = false;
    }
  }, [isEnrolled, initialize]);

  const handleStopSpeaking = useCallback(() => {
    stopSpeaking();
  }, []);

  const handleSpeakResponse = useCallback(async (text: string) => {
    if (isReady()) {
      await speakResponse(text);
    }
  }, []);

  const onEnrollmentComplete = useCallback((newEnrollment: VoiceEnrollment) => {
    setEnrollmentState(newEnrollment);
    setIsEnrolled(true);
    setEnrollment(newEnrollment);
    setShowEnrollment(false);
  }, []);

  // Set transcription callback (fires on EndOfTurn — triggers send)
  const setOnTranscription = useCallback((cb: (text: string) => void) => {
    onTranscriptionRef.current = cb;
  }, []);

  // Set interim transcript callback (fires on every partial — updates UI only)
  const setOnInterimTranscript = useCallback((cb: (text: string) => void) => {
    onInterimTranscriptRef.current = cb;
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      destroyVoiceService();
      kokoroTTS.disconnect();
    };
  }, []);

  return {
    voiceState,
    isEnrolled,
    showEnrollment,
    setShowEnrollment,
    modelProgress,
    availableVoices,
    error,
    handleMicPress,
    handleStopSpeaking,
    handleSpeakResponse,
    onEnrollmentComplete,
    setOnTranscription,
    setOnInterimTranscript,
    setPreferences,
  };
}
